import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type RecommendationItem } from '../api/endpoints';
import { ApiException } from '../api/client';
import { Skeleton, ErrorState } from './states';
import { RecipeCard } from './RecipeCard';

// Секция «Что готовим сегодня» — рекомендации из холодильника. Вынесена из HomeScreen,
// чтобы главный экран только оркестрировал секции (спека §3/§4). Поведение сохранено.
export function TodaySection() {
  const navigate = useNavigate();
  const [items, setItems] = useState<RecommendationItem[] | null>(null);
  const [pantryEmpty, setPantryEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pantry, rec] = await Promise.all([endpoints.getPantry(), endpoints.getRecommendations()]);
      setPantryEmpty(pantry.length === 0);
      setItems(rec.items);
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить рекомендации');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  return (
    <section aria-label="Что готовим сегодня">
      <h2 className="mb-3 text-[17px] font-bold text-ink">Что готовим сегодня</h2>

      {pantryEmpty && !loading && (
        <div className="mb-4 rounded-2xl bg-gradient-to-br from-sand/55 to-sand/20 p-5 shadow-card">
          <p className="font-semibold text-ink">Холодильник пуст</p>
          <p className="mb-3 text-sm text-ink/70">Добавьте, что есть дома — и подберём блюда из ваших продуктов.</p>
          <button
            onClick={() => navigate('/pantry')}
            className="rounded-xl bg-primary-600 px-4 py-2.5 font-semibold text-white shadow-card transition active:scale-[0.98]"
          >
            Заполнить холодильник
          </button>
        </div>
      )}

      {loading && (
        <div className="space-y-4" data-testid="home-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      )}

      {!loading && error && <ErrorState message={error} onRetry={() => void load()} />}

      {!loading && !error && items && (
        <>
          <div className="stagger space-y-4">
            {items.map((it) => (
              <RecipeCard
                key={it.recipeId}
                title={it.recipe?.title ?? 'Рецепт'}
                imageUrl={it.recipe?.imageUrl}
                cookTimeMin={it.recipe?.cookTimeMin}
                servings={it.recipe?.servings}
                reason={it.reason}
                onClick={() => navigate(`/recipe/${it.recipeId}`)}
              />
            ))}
          </div>
          <button
            onClick={() => void load()}
            className="mt-6 w-full rounded-xl border border-primary-600/70 px-4 py-3 font-semibold text-primary-600 transition hover:bg-primary-50 active:scale-[0.99]"
          >
            Обновить
          </button>
        </>
      )}
    </section>
  );
}
